import { z } from "zod";

export function useValidation() {
  const { t } = useI18n();

  const signInSchema = z.object({
    mobile: z
      .string({
        required_error: t("validation.required", { name: t("name.mobile") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.mobile") }))
      .regex(
        /^[0-9]+$/,
        t("validation.digits_only", { name: t("name.mobile") })
      )
      .min(
        7,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      )
      .max(
        11,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      ),
    country_id: z.number(),
  });

  const changeEmailSchema = z.object({
    email: z
      .string({
        required_error: t("validation.required", { name: t("name.email") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.email") }))
      .email(t("validation.email")),
  });

  const verificationCodeSchema = z.object({
    verification_code: z
      .string({
        required_error: t("validation.fill"),
        invalid_type_error: t("validation.digits_only", {
          name: t("name.code"),
        }),
      })
      .min(5, t("validation.fill")),
  });

  const profileSchema = z.object({
    name: z
      .string({
        required_error: t("validation.required", { name: t("name.full_name") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.full_name") }))
      .min(3, t("validation.min", { name: t("name.full_name"), min: 3 }))
      .max(255, t("validation.max", { name: t("name.full_name"), max: 255 })),
    email: z.string().email(t("validation.email")).optional().or(z.literal("")),
    gender: z.string().optional(),
    birthday: z.date().optional().nullable(),
  });

  const createBusinessDetailsRequest = z.object({
    business_name: z
      .string({
        required_error: t("validation.required", {
          name: t("name.business_name"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.business_name") }))
      .min(3, t("validation.min", { name: t("name.business_name"), min: 3 }))
      .max(60, t("validation.max", { name: t("name.business_name"), max: 60 })),
    country_id: z.number({
      required_error: t("validation.required", { name: t("name.country") }),
    }),
  });

  // about_business:
  //     z.string()
  //       .trim()
  //       .max(1000, t("validation.max", { name: t('name.business_name'), max: 1000 }))
  //       .optional(),

  const createYourAccountRequest = z.object({
    first_name: z
      .string({
        required_error: t("validation.required", {
          name: t("name.first_name"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.first_name") }))
      .min(2, t("validation.min", { name: t("name.first_name"), min: 2 }))
      .max(255, t("validation.max", { name: t("name.first_name"), max: 64 })),
    last_name: z
      .string({
        required_error: t("validation.required", { name: t("name.last_name") }),
      })
      .max(64, t("validation.max", { name: t("name.last_name"), max: 64 }))
      .optional(),
    business_email: z
      .string({
        required_error: t("validation.required", { name: t("name.email") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.email") }))
      .email(t("validation.email")),
    mobile: z
      .string({
        required_error: t("validation.required", { name: t("name.mobile") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.mobile") }))
      .regex(
        /^[0-9]+$/,
        t("validation.digits_only", { name: t("name.mobile") })
      )
      .min(
        7,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      )
      .max(
        11,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      ),
  });

  const cancelBookingSchema = z.object({
    cancel_reason: z
      .string({
        required_error: t("validation.required", {
          name: t("name.cancel_reason"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.cancel_reason") }))
      .min(3, t("validation.min", { name: t("name.cancel_reason"), min: 3 }))
      .max(
        250,
        t("validation.max", { name: t("name.cancel_reason"), max: 250 })
      ),
    status: z.string(),
  });

  const contactUsSchema = z.object({
    subject: z
      .string({
        required_error: t("validation.required", { name: t("name.subject") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.subject") })),
    name: z
      .string({
        required_error: t("validation.required", {
          name: t("label.your_name"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("label.your_name") }))
      .min(3, t("validation.min", { name: t("label.your_name"), min: 3 }))
      .max(255, t("validation.max", { name: t("label.your_name"), max: 255 })),
    email: z
      .string({
        required_error: t("validation.required", { name: t("name.email") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.email") }))
      .email(t("validation.email")),
    message: z
      .string({
        required_error: t("validation.required", { name: t("name.message") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.message") }))
      .min(3, t("validation.min", { name: t("name.message"), min: 3 }))
      .max(255, t("validation.max", { name: t("name.message"), max: 500 })),
    mobile: z
      .string()
      .trim()
      .regex(
        /^[0-9]+$/,
        t("validation.digits_only", { name: t("name.mobile") })
      )
      .min(
        7,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      )
      .max(
        11,
        t("validation.min_max", { name: t("name.mobile"), min: 7, max: 11 })
      )
      .optional(),
  });

  const contactSalesSchema = z.object({
    name: z
      .string({
        required_error: t("validation.required", {
          name: t("label.your_name"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("label.your_name") }))
      .min(3, t("validation.min", { name: t("label.your_name"), min: 3 }))
      .max(255, t("validation.max", { name: t("label.your_name"), max: 255 })),
    email: z
      .string({
        required_error: t("validation.required", { name: t("name.email") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.email") }))
      .email(t("validation.email")),
    country_id: z.number({
      required_error: t("validation.required", { name: t("name.country") }),
    }),
    phone: z
      .string({
        required_error: t("validation.required", { name: t("name.phone") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.phone") }))
      .regex(/^[0-9]+$/, t("validation.digits_only", { name: t("name.phone") }))
      .min(
        7,
        t("validation.min_max", { name: t("name.phone"), min: 7, max: 11 })
      )
      .max(
        11,
        t("validation.min_max", { name: t("name.phone"), min: 7, max: 11 })
      ),
    business_name: z
      .string({
        required_error: t("validation.required", {
          name: t("name.business_name"),
        }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.business_name") }))
      .min(3, t("validation.min", { name: t("name.business_name"), min: 3 }))
      .max(60, t("validation.max", { name: t("name.business_name"), max: 60 })),
    team_size: z
      .string({
        required_error: t("validation.required", { name: t("name.team_size") }),
      })
      .trim()
      .regex(
        /^[0-9]+$/,
        t("validation.digits_only", { name: t("name.team_size") })
      ),
    message: z
      .string({
        required_error: t("validation.required", { name: t("name.message") }),
      })
      .trim()
      .min(1, t("validation.required", { name: t("name.message") }))
      .min(3, t("validation.min", { name: t("name.message"), min: 3 }))
      .max(255, t("validation.max", { name: t("name.message"), max: 500 })),
  });

  const recipientDetailsSchema = z.object({
    sender_name: z
      .string()
      .nonempty(t("validation.required", { name: t("label.sender_name") })),
    recipient_name: z
      .string()
      .nonempty(t("validation.required", { name: t("label.recipient_name") })),
    recipient_email: z
      .string({
        required_error: t("validation.required", {
          name: t("label.recipient_email"),
        }),
      })
      .email(t("validation.email")),
    personal_message: z
      .string()
      .optional()
      .refine((val) => !val || (val.length >= 3 && val.length <= 255), {
        message: t("validation.min", {
          name: t("label.personal_message"),
          min: 3,
          max: 500,
        }),
      }),
  });

  return {
    signInSchema,
    verificationCodeSchema,
    profileSchema,
    createBusinessDetailsRequest,
    createYourAccountRequest,
    cancelBookingSchema,
    contactUsSchema,
    contactSalesSchema,
    recipientDetailsSchema,
    changeEmailSchema,
  };
}
